package com.daytimegaming.ourdailyfinances.presentation.dashboard

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.dashboard.DashboardEventBus
import com.daytimegaming.ourdailyfinances.domain.model.SecurityPerformance
import com.daytimegaming.ourdailyfinances.domain.plaid.PlaidItemsEventBus
import com.daytimegaming.ourdailyfinances.domain.realestate.RealEstateEventBus
import com.daytimegaming.ourdailyfinances.domain.repository.TransactionRepository
import com.daytimegaming.ourdailyfinances.domain.usecase.DashboardUseCase
import com.daytimegaming.ourdailyfinances.presentation.components.BalanceTimeframe
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.launch

class DashboardDetailViewModel(
    private val dashboardId: String,
    private val dashboardUseCase: DashboardUseCase,
    private val transactionRepository: TransactionRepository,
    private val realEstateEventBus: RealEstateEventBus,
    private val plaidItemsEventBus: PlaidItemsEventBus,
    private val dashboardEventBus: DashboardEventBus,
) : ViewModel() {

    private val _state = MutableStateFlow<DashboardDetailScreenState>(DashboardDetailScreenState.Loading)
    val state = _state.asStateFlow()

    private val _selectedTimeframe = MutableStateFlow(BalanceTimeframe.SixMonths)
    val selectedTimeframe: StateFlow<BalanceTimeframe> = _selectedTimeframe.asStateFlow()

    fun setSelectedTimeframe(timeframe: BalanceTimeframe) {
        _selectedTimeframe.value = timeframe
    }

    private val _securityPerformance = MutableStateFlow<SecurityPerformance?>(null)
    private val _isSecurityPerformanceLoading = MutableStateFlow(false)
    private var hasLoadedSecurityPerformance = false
    private var loadJob: Job? = null

    private fun loadSecurityPerformance() {
        if (!hasLoadedSecurityPerformance) {
            hasLoadedSecurityPerformance = true
            _isSecurityPerformanceLoading.value = true
            viewModelScope.launch {
                try {
                    val response = transactionRepository.getSecurityPerformance(dashboardId = dashboardId)
                    if (response is Response.Success) {
                        _securityPerformance.value = response.data
                    }
                } catch (e: Exception) {
                    println("ERROR: getSecurityPerformance failed: $e")
                } finally {
                    _isSecurityPerformanceLoading.value = false
                }
            }
        }
    }

    init {
        load()
        viewModelScope.launch {
            realEstateEventBus.events.collect { load() }
        }
        viewModelScope.launch {
            plaidItemsEventBus.events.collect { load() }
        }
        viewModelScope.launch {
            dashboardEventBus.events.collect { changedId ->
                if (changedId == null || changedId == dashboardId) {
                    load()
                }
            }
        }
    }

    fun load() {
        viewModelScope.launch {
            transactionRepository.refresh()
        }
        loadJob?.cancel()
        loadJob = viewModelScope.launch {
            combine(
                dashboardUseCase.GetDashboardDetail(dashboardId),
                transactionRepository.transactions,
                transactionRepository.investments,
                _securityPerformance,
                _isSecurityPerformanceLoading,
            ) { dashboardResponse, transactionsResponse, investmentsResponse, securityPerformance, isSecurityPerformanceLoading ->
                when (dashboardResponse) {
                    is Response.Loading -> DashboardDetailScreenState.Loading
                    is Response.Error -> DashboardDetailScreenState.Error(dashboardResponse.message)
                    is Response.Success -> {
                        val detail = dashboardResponse.data
                        val dashboardAccountIds = detail.accounts.map { it.accountId }.toSet()
                        val repoTx = if (transactionsResponse is Response.Success) {
                            transactionsResponse.data.filter { it.accountId in dashboardAccountIds }
                        } else {
                            emptyList()
                        }
                        val mergedTransactions = (detail.transactions + repoTx)
                            .distinctBy { it.transactionId }
                            .sortedByDescending { it.date }
                        val mergedDetail = detail.copy(transactions = mergedTransactions)
                        val dashboardInvestments = detail.investments ?: (investmentsResponse as? Response.Success)?.data
                        val detailHoldings = dashboardInvestments?.holdings ?: emptyList()
                        val repoHoldings = (investmentsResponse as? Response.Success)?.data?.holdings ?: emptyList()
                        val mergedHoldings = (detailHoldings + repoHoldings).distinctBy { "${it.accountId}_${it.securityId}" }
                        val holdings = mergedHoldings.filter { it.accountId in dashboardAccountIds }

                        val hasInvestment = detail.accounts.any { it.type.lowercase() == "investment" }
                        val isLoadingPerf = if (hasInvestment && !hasLoadedSecurityPerformance) {
                            loadSecurityPerformance()
                            true
                        } else {
                            isSecurityPerformanceLoading
                        }

                        DashboardDetailScreenState.Loaded(
                            detail = mergedDetail,
                            securityPerformance = securityPerformance,
                            holdings = holdings,
                            isSecurityPerformanceLoading = isLoadingPerf,
                        )
                    }
                }
            }.collect { _state.value = it }
        }
    }
}
